import { formatMoney } from "@sylviq/ui-core";
import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { AccountPicker } from "@/components/Pickers";
import {
  conversionAccountKind,
  reviewConversionLines,
  type ConversionAccountKind,
  type ConversionDraftLine,
  type ConversionReview,
} from "@/lib/conversion-balances";
import {
  ApiRequestError,
  type ChartAccount,
  type FinanceConversionBalanceResponse,
  getConversionBalances,
  listChartAccounts,
  postJournalEntry,
  saveConversionBalances,
} from "@/lib/api";

interface DraftLine {
  uid: string;
  account_id: string;
  debit: string;
  credit: string;
}

function blankLine(): DraftLine {
  return { uid: crypto.randomUUID(), account_id: "", debit: "", credit: "" };
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

function money(cents: number): string {
  return formatMoney({ cents, currency: "USD" });
}

function centsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

function dollarsToCents(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return 0;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) ? minor : null;
}

function toErrorMessage(cause: unknown): string {
  if (cause instanceof ApiRequestError && cause.status === 409) {
    return "This conversion journal is already posted. Reverse it before replacing it.";
  }
  return cause instanceof Error ? cause.message : String(cause);
}

function linesFromResponse(
  lines: FinanceConversionBalanceResponse["lines"],
): DraftLine[] {
  if (lines.length === 0) return [blankLine(), blankLine()];
  return lines.map((line) => ({
    uid: crypto.randomUUID(),
    account_id: line.account_id,
    debit: line.debit_cents > 0 ? centsToInput(line.debit_cents) : "",
    credit: line.credit_cents > 0 ? centsToInput(line.credit_cents) : "",
  }));
}

function prepareLines(
  drafts: readonly DraftLine[],
  accounts: readonly ChartAccount[],
): { lines: ConversionDraftLine[] } | { error: string } {
  const filled = drafts.filter(
    (line) => line.account_id || line.debit.trim() || line.credit.trim(),
  );
  const lines: ConversionDraftLine[] = [];
  for (const line of filled) {
    const debit = dollarsToCents(line.debit);
    const credit = dollarsToCents(line.credit);
    if (
      !line.account_id ||
      debit === null ||
      credit === null ||
      !((debit > 0 && credit === 0) || (credit > 0 && debit === 0))
    ) {
      return {
        error:
          "Each line needs an account and either a debit or a credit, not both and not zero.",
      };
    }
    const account = accounts.find((item) => item.id === line.account_id);
    lines.push({
      account_id: line.account_id,
      debit_cents: debit,
      credit_cents: credit,
      kind: conversionAccountKind(account?.account_type, account?.sub_type),
    });
  }
  return { lines };
}

function sameLines(
  drafts: readonly ConversionDraftLine[],
  saved: readonly { account_id: string; debit_cents: number; credit_cents: number }[],
): boolean {
  if (drafts.length !== saved.length) return false;
  return drafts.every(
    (line, index) =>
      line.account_id === saved[index]?.account_id &&
      line.debit_cents === saved[index]?.debit_cents &&
      line.credit_cents === saved[index]?.credit_cents,
  );
}

function blockReason(
  prepared: { lines: ConversionDraftLine[] } | { error: string },
  booksStart: string,
  openInvoices: number,
  openBills: number,
  dateChecked: boolean,
): { review: ConversionReview | null; reason: string | null } {
  if (!dateChecked) {
    return {
      review: null,
      reason: "Check open invoices and bills for this start date before saving.",
    };
  }
  if ("error" in prepared) return { review: null, reason: prepared.error };
  const reviewed = reviewConversionLines(booksStart, prepared.lines, {
    invoices_cents: openInvoices,
    bills_cents: openBills,
  });
  if (!reviewed.ok) {
    const reason =
      reviewed.error === "no_previous_day"
        ? "The books start date has no previous day."
        : reviewed.error === "overflow"
          ? "These amounts are too large to add together."
          : "Each line needs an account and either a debit or a credit, not both and not zero.";
    return { review: null, reason };
  }
  const { review } = reviewed;
  const reasons: string[] = [];
  if (review.line_count < 2) {
    reasons.push("A conversion journal needs at least two lines.");
  } else if (!review.balanced) {
    reasons.push(
      "Debits and credits must be equal before this conversion journal can be saved.",
    );
  }
  if (!review.receivables_match) {
    reasons.push(
      `Receivables on this journal are ${money(review.receivables_cents)} and open invoices dated on or before the conversion date are ${money(review.open_invoices_cents)}. They must be equal before this journal can be saved.`,
    );
  }
  if (!review.payables_match) {
    reasons.push(
      `Payables on this journal are ${money(review.payables_cents)} and open bills dated on or before the conversion date are ${money(review.open_bills_cents)}. They must be equal before this journal can be saved.`,
    );
  }
  return { review, reason: reasons.length > 0 ? reasons.join(" ") : null };
}

function kindLabel(kind: ConversionAccountKind): string | null {
  if (kind === "receivable") return "Receivable";
  if (kind === "payable") return "Payable";
  return null;
}

export function OpeningBalancesPage() {
  const [booksStart, setBooksStart] = useState(todayInput);
  const [checkedStart, setCheckedStart] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<ChartAccount[]>([]);
  const [lines, setLines] = useState<DraftLine[]>([blankLine(), blankLine()]);
  const [saved, setSaved] = useState<FinanceConversionBalanceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [confirmPost, setConfirmPost] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const applyResponse = useCallback((response: FinanceConversionBalanceResponse) => {
    setSaved(response);
    if (response.books_start_date) setBooksStart(response.books_start_date);
    setLines(linesFromResponse(response.lines));
  }, []);

  const load = useCallback(
    (start: string) => {
      setLoading(true);
      setError(null);
      setActionError(null);
      Promise.all([listChartAccounts(), getConversionBalances(start)])
        .then(([chart, response]) => {
          setAccounts(chart);
          applyResponse(response);
          setCheckedStart(response.books_start_date ?? start);
        })
        .catch((cause: unknown) => setError(toErrorMessage(cause)))
        .finally(() => setLoading(false));
    },
    [applyResponse],
  );

  useEffect(() => {
    load(todayInput());
  }, [load]);

  const posted = saved?.status === "posted";
  const openInvoices = saved?.open_invoices_cents ?? 0;
  const openBills = saved?.open_bills_cents ?? 0;
  const prepared = prepareLines(lines, accounts);
  const { review, reason } = blockReason(
    prepared,
    booksStart,
    openInvoices,
    openBills,
    checkedStart === booksStart && saved !== null,
  );
  const editable = !posted && !loading && !saving && !posting;
  const canSave = editable && reason === null && review?.save_allowed === true;
  const savedLinesMatch =
    saved !== null &&
    "lines" in prepared &&
    saved.books_start_date === booksStart &&
    sameLines(prepared.lines, saved.lines);
  const canPost =
    editable &&
    saved?.status === "draft" &&
    Boolean(saved.posting_review_sha256) &&
    Boolean(saved.journal_entry_id) &&
    savedLinesMatch &&
    review?.save_allowed === true;

  const updateLine = (index: number, patch: Partial<DraftLine>) => {
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? { ...line, ...patch } : line,
      ),
    );
  };

  const checkBalances = (event: FormEvent) => {
    event.preventDefault();
    load(booksStart);
  };

  const saveDraft = async () => {
    if (!canSave || !("lines" in prepared)) return;
    setSaving(true);
    setActionError(null);
    try {
      const response = await saveConversionBalances({
        books_start_date: booksStart,
        lines: prepared.lines.map((line) => ({
          account_id: line.account_id,
          debit_cents: line.debit_cents,
          credit_cents: line.credit_cents,
        })),
      });
      applyResponse(response);
      setCheckedStart(response.books_start_date ?? booksStart);
    } catch (cause) {
      setActionError(toErrorMessage(cause));
    } finally {
      setSaving(false);
    }
  };

  const postDraft = async () => {
    if (!saved?.journal_entry_id || !saved.posting_review_sha256) return;
    setPosting(true);
    setActionError(null);
    try {
      await postJournalEntry(saved.journal_entry_id, saved.posting_review_sha256);
      setConfirmPost(false);
      const response = await getConversionBalances(booksStart);
      setAccounts(await listChartAccounts());
      applyResponse(response);
      setCheckedStart(response.books_start_date ?? booksStart);
    } catch (cause) {
      setConfirmPost(false);
      setActionError(toErrorMessage(cause));
    } finally {
      setPosting(false);
    }
  };

  const entryDate = review?.entry_date ?? saved?.entry_date;
  const debitCents = review?.debit_cents ?? saved?.debit_cents ?? 0;
  const creditCents = review?.credit_cents ?? saved?.credit_cents ?? 0;
  const receivablesCents = review?.receivables_cents ?? saved?.receivables_cents ?? 0;
  const payablesCents = review?.payables_cents ?? saved?.payables_cents ?? 0;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Opening balances"
        description="One journal dated the day before these books start. Receivables on that journal must equal open invoices, and payables must equal open bills. Saving keeps a draft. Posting records it in the books."
      />

      {error ? (
        <div
          role="alert"
          className="surface grid gap-2 rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          <p>{error}</p>
          <div>
            <Button type="button" variant="secondary" size="sm" onClick={() => load(booksStart)}>
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      <form
        onSubmit={checkBalances}
        className="surface grid gap-3 rounded-2xl p-5 sm:grid-cols-[auto_auto] sm:items-end"
      >
        <label htmlFor="books-start" className="grid gap-1 text-sm font-medium">
          Books start
          <Input
            id="books-start"
            type="date"
            value={booksStart}
            onChange={(event) => setBooksStart(event.target.value)}
            required
            disabled={posted || loading || saving || posting}
          />
        </label>
        <Button type="submit" variant="secondary" disabled={posted || loading || saving || posting}>
          {loading ? "Checking…" : "Check open invoices and bills"}
        </Button>
        <p className="text-sm text-[var(--muted-foreground)] sm:col-span-2">
          The journal is dated {entryDate ?? "the day before the books start"}.
          {posted ? " This conversion journal is posted." : " A draft is not in the books yet."}
        </p>
      </form>

      <section className="surface grid gap-3 rounded-2xl p-5" aria-live="polite">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-sm font-semibold">Trial balance check</h2>
          <Badge tone={posted ? "success" : "neutral"}>
            {posted ? "Posted" : saved?.status === "draft" ? "Draft" : "Not saved"}
          </Badge>
        </div>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-[var(--muted-foreground)]">Debits</dt>
            <dd className="tabular-nums">{money(debitCents)}</dd>
          </div>
          <div>
            <dt className="text-[var(--muted-foreground)]">Credits</dt>
            <dd className="tabular-nums">{money(creditCents)}</dd>
          </div>
          <div>
            <dt className="text-[var(--muted-foreground)]">Receivables on this journal</dt>
            <dd className="tabular-nums">{money(receivablesCents)}</dd>
          </div>
          <div>
            <dt className="text-[var(--muted-foreground)]">Open invoices</dt>
            <dd className="tabular-nums">{money(openInvoices)}</dd>
          </div>
          <div>
            <dt className="text-[var(--muted-foreground)]">Payables on this journal</dt>
            <dd className="tabular-nums">{money(payablesCents)}</dd>
          </div>
          <div>
            <dt className="text-[var(--muted-foreground)]">Open bills</dt>
            <dd className="tabular-nums">{money(openBills)}</dd>
          </div>
        </dl>
        {reason ? <p className="text-sm">{reason}</p> : <p className="text-sm">Debits equal credits, and receivables and payables match the open documents.</p>}
      </section>

      {actionError ? (
        <div
          role="alert"
          className="surface grid gap-2 rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          <p>{actionError}</p>
          <div>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={saving || posting}
              onClick={() => void (canPost ? setConfirmPost(true) : saveDraft())}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {posted && saved?.journal_entry_id ? (
        <p className="text-sm">
          This conversion journal is posted. Reverse it in the general ledger before replacing it.{" "}
          <Link
            to="/journal/$entryId"
            params={{ entryId: saved.journal_entry_id }}
            className="text-[var(--action)] underline-offset-4 hover:underline"
          >
            Open the journal
          </Link>
        </p>
      ) : null}

      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void saveDraft();
        }}
      >
        <fieldset className="grid gap-3" disabled={!editable}>
          <legend className="text-sm font-medium">Conversion journal lines</legend>
          {lines.map((line, index) => {
            const account = accounts.find((item) => item.id === line.account_id);
            const kind = account
              ? kindLabel(conversionAccountKind(account.account_type, account.sub_type))
              : null;
            return (
              <div
                key={line.uid}
                className="surface grid gap-2 rounded-xl border border-[var(--border)] p-3"
              >
                <div className="grid gap-2 sm:grid-cols-[2fr_1fr_1fr_auto] sm:items-end">
                  <label htmlFor={`conversion-line-${index}-account`} className="grid gap-1 text-xs font-medium">
                    Account
                    <AccountPicker
                      id={`conversion-line-${index}-account`}
                      value={line.account_id}
                      onChange={(value) => updateLine(index, { account_id: value })}
                      disabled={!editable}
                      required
                      ariaLabel={`Account for line ${index + 1}`}
                    />
                  </label>
                  <label htmlFor={`conversion-line-${index}-debit`} className="grid gap-1 text-xs font-medium">
                    Debit (USD)
                    <Input
                      id={`conversion-line-${index}-debit`}
                      type="number"
                      value={line.debit}
                      onChange={(event) => updateLine(index, { debit: event.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={!editable}
                    />
                  </label>
                  <label htmlFor={`conversion-line-${index}-credit`} className="grid gap-1 text-xs font-medium">
                    Credit (USD)
                    <Input
                      id={`conversion-line-${index}-credit`}
                      type="number"
                      value={line.credit}
                      onChange={(event) => updateLine(index, { credit: event.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      inputMode="decimal"
                      disabled={!editable}
                    />
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setLines((current) =>
                        current.length <= 2
                          ? current
                          : current.filter((_, lineIndex) => lineIndex !== index),
                      )
                    }
                    disabled={!editable || lines.length <= 2}
                    aria-label={`Remove line ${index + 1}`}
                  >
                    Remove
                  </Button>
                </div>
                {kind ? <p className="text-xs text-[var(--muted-foreground)]">{kind}</p> : null}
              </div>
            );
          })}
          <div>
            <Button
              type="button"
              variant="secondary"
              disabled={!editable}
              onClick={() => setLines((current) => [...current, blankLine()])}
            >
              Add line
            </Button>
          </div>
        </fieldset>
        {posted ? null : (
          <div className="flex flex-wrap gap-2">
            <Button type="submit" disabled={!canSave}>
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button type="button" variant="outline" disabled={!canPost} onClick={() => setConfirmPost(true)}>
              Post to the books
            </Button>
          </div>
        )}
      </form>

      <Dialog
        open={confirmPost}
        onOpenChange={setConfirmPost}
        title="Post this conversion journal?"
        description={
          entryDate
            ? `Posting records the journal dated ${entryDate} in the books. Saving the draft does not.`
            : "Posting records this journal in the books. Saving the draft does not."
        }
      >
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" disabled={posting} onClick={() => setConfirmPost(false)}>
            Keep as draft
          </Button>
          <Button type="button" disabled={posting} onClick={() => void postDraft()}>
            {posting ? "Posting…" : "Post journal"}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
